import { useEffect, useState } from 'react'
import { api } from '../api'

interface Category {
  id: number
  name: string
  slug: string
  type: string
  parent_id: number | null
  sort_order: number
  children?: Category[]
}

export default function CategoriesPage() {
  const [list, setList] = useState<Category[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Category | null>(null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [type, setType] = useState('industry')
  const [parentId, setParentId] = useState('')
  const [sortOrder, setSortOrder] = useState(0)
  const [filter, setFilter] = useState('')

  const fetchList = async () => {
    const res = await api.get('/api/admin/categories')
    if (res.success) setList(res.data)
  }

  useEffect(() => { fetchList() }, [])

  const resetForm = () => {
    setName(''); setSlug(''); setType('industry'); setParentId(''); setSortOrder(0)
    setEditing(null); setShowForm(false)
  }

  const handleEdit = (cat: Category) => {
    setEditing(cat)
    setName(cat.name)
    setSlug(cat.slug || '')
    setType(cat.type)
    setParentId(cat.parent_id ? String(cat.parent_id) : '')
    setSortOrder(cat.sort_order)
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { alert('请输入名称'); return }

    const body = {
      name, slug, type,
      parent_id: parentId ? Number(parentId) : null,
      sort_order: sortOrder
    }
    const res = editing
      ? await api.put(`/api/admin/categories/${editing.id}`, body)
      : await api.post('/api/admin/categories', body)

    if (res.success) {
      resetForm()
      fetchList()
    } else {
      alert(res.error || '操作失败')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('确定删除？')) return
    const res = await api.delete(`/api/admin/categories/${id}`)
    if (res.success) fetchList()
    else alert(res.error || '删除失败')
  }

  // 获取可选的父级分类（同类型的一级分类，排除自己）
  const getParentOptions = () => {
    return list.filter((c) =>
      c.type === type &&
      !c.parent_id &&
      (!editing || c.id !== editing.id)
    )
  }

  // 根据 filter 过滤并展平树
  const getFilteredRows = (): (Category & { _depth: number })[] => {
    const rows: (Category & { _depth: number })[] = []
    for (const item of list) {
      if (!filter || item.type === filter) {
        rows.push({ ...item, _depth: 0 })
        if (item.children) {
          for (const child of item.children) {
            rows.push({ ...child, _depth: 1 })
          }
        }
      }
    }
    return rows
  }

  const filtered = getFilteredRows()

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">分类管理</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true) }}>+ 新增分类</button>
      </div>

      <div className="search-bar">
        <select className="form-input" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="">全部类型</option>
          <option value="industry">行业</option>
          <option value="region">地区</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>{editing ? '编辑分类' : '新增分类'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
              <div className="form-group">
                <label className="form-label">名称 *</label>
                <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Slug</label>
                <input className="form-input" value={slug} onChange={(e) => setSlug(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">类型</label>
                <select className="form-input" value={type} onChange={(e) => { setType(e.target.value); setParentId('') }}>
                  <option value="industry">行业</option>
                  <option value="region">地区</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">父级分类</label>
                <select className="form-input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                  <option value="">无（一级分类）</option>
                  {getParentOptions().map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">排序</label>
                <input className="form-input" type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn btn-primary btn-sm">保存</button>
                <button type="button" className="btn btn-outline btn-sm" onClick={resetForm}>取消</button>
              </div>
            </div>
          </form>
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>名称</th>
            <th>Slug</th>
            <th>类型</th>
            <th>父级</th>
            <th>排序</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={7} style={{ textAlign: 'center' }}>暂无数据</td></tr>
          ) : (
            filtered.map((item) => {
              const parentName = item.parent_id
                ? list.find((c) => c.id === item.parent_id)?.name || '-'
                : '-'
              return (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td style={{ paddingLeft: item._depth ? '24px' : undefined }}>
                    {item._depth ? '└ ' : ''}{item.name}
                  </td>
                  <td>{item.slug || '-'}</td>
                  <td>
                    <span className={`badge ${item.type === 'industry' ? 'badge-success' : 'badge-secondary'}`}>
                      {item.type === 'industry' ? '行业' : '地区'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>{parentName}</td>
                  <td>{item.sort_order}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => handleEdit(item)}>编辑</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)}>删除</button>
                    </div>
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
